import { useEffect, useState, useCallback } from 'react';
import { getTools, deleteTool } from '../utils/api';
import ToolForm from '../components/tools/ToolForm';
import ToolIntakeModal from '../components/tools/ToolIntakeModal';
import ToolBulkIntakeModal from '../components/tools/ToolBulkIntakeModal';
import EmptyState from '../components/common/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';

const TOOL_CATEGORIES = ['power', 'hand', 'measuring', 'safety', 'garden', 'plumbing', 'electrical', 'automotive', 'other'];
const CAT_LABEL = (c) => c.charAt(0).toUpperCase() + c.slice(1);

const CONDITION_COLOR = { excellent: 'success', good: 'info', fair: 'warning', poor: 'danger' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-CA') : null;
const fmtMoney = (n) => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const ToolCard = ({ tool, onEdit, onDelete }) => (
  <div className="tool-card">
    <div className="tool-card-main">
      <div className="tool-card-title">
        <span className="tool-name">{tool.name}</span>
        <span className={`badge badge-${CONDITION_COLOR[tool.condition] || 'info'}`}>{tool.condition}</span>
      </div>
      {(tool.brand || tool.modelNumber) && (
        <span className="tool-meta">{[tool.brand, tool.modelNumber].filter(Boolean).join(' · ')}</span>
      )}
      {tool.location && <span className="tool-meta">📍 {tool.location}</span>}
      <div className="tool-meta-row">
        {fmtMoney(tool.purchasePrice) && <span className="tool-meta">Paid {fmtMoney(tool.purchasePrice)}</span>}
        {tool.warrantyExpiry && <span className="tool-meta">Warranty until {fmtDate(tool.warrantyExpiry)}</span>}
      </div>
      {tool.notes && <span className="tool-notes">{tool.notes}</span>}
    </div>
    <div className="tool-card-actions">
      <button className="btn btn-sm btn-secondary" onClick={onEdit}>Edit</button>
      <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
    </div>
  </div>
);

const BatteryCard = ({ tool, onEdit, onDelete }) => (
  <div className="tool-card battery-card">
    <div className="tool-card-main">
      <div className="tool-card-title">
        <span className="tool-name">{tool.name}</span>
        {tool.quantity > 1 && <span className="battery-qty-badge">×{tool.quantity}</span>}
        <span className={`badge badge-${CONDITION_COLOR[tool.condition] || 'info'}`}>{tool.condition}</span>
      </div>
      <div className="tool-meta-row">
        {tool.platform && <span className="tool-meta">{tool.platform}</span>}
        {tool.voltage && <span className="tool-meta">{tool.voltage}</span>}
        {tool.ampHours != null && <span className="tool-meta">{tool.ampHours}Ah</span>}
        {(tool.brand || tool.modelNumber) && (
          <span className="tool-meta">{[tool.brand, tool.modelNumber].filter(Boolean).join(' · ')}</span>
        )}
      </div>
      {tool.location && <span className="tool-meta">📍 {tool.location}</span>}
      <div className="tool-meta-row">
        {fmtMoney(tool.purchasePrice) && <span className="tool-meta">Paid {fmtMoney(tool.purchasePrice)}</span>}
        {tool.warrantyExpiry && <span className="tool-meta">Warranty until {fmtDate(tool.warrantyExpiry)}</span>}
      </div>
      {tool.notes && <span className="tool-notes">{tool.notes}</span>}
    </div>
    <div className="tool-card-actions">
      <button className="btn btn-sm btn-secondary" onClick={onEdit}>Edit</button>
      <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
    </div>
  </div>
);

const Tools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [filter, setFilter] = useState('all');
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTools();
      setTools(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (saved) => {
    setTools((prev) => {
      const idx = prev.findIndex((t) => t._id === saved._id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [...prev, saved].sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
    });
  };

  const handleDelete = async (tool) => {
    const label = tool.category === 'battery' ? 'battery' : 'tool';
    if (!(await confirm({ title: `Delete ${label}`, message: `Remove "${tool.name}" from your inventory?`, confirmLabel: 'Delete', danger: true }))) return;
    try {
      await deleteTool(tool._id);
      setTools((prev) => prev.filter((t) => t._id !== tool._id));
      showToast(`${CAT_LABEL(label)} removed`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const batteries = tools.filter((t) => t.category === 'battery');
  const regularTools = tools.filter((t) => t.category !== 'battery');

  const displayedTools = filter === 'all'
    ? regularTools
    : regularTools.filter((t) => t.category === filter);

  const grouped = TOOL_CATEGORIES.reduce((acc, cat) => {
    const catTools = displayedTools.filter((t) => t.category === cat);
    if (catTools.length) acc[cat] = catTools;
    return acc;
  }, {});

  const catCounts = TOOL_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = regularTools.filter((t) => t.category === cat).length;
    return acc;
  }, {});

  const openAdd = (asBattery = false) => {
    setEditTool(asBattery ? { category: 'battery' } : null);
    setShowForm(true);
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Tool Inventory</h1>
          {tools.length > 0 && (
            <p className="page-subtitle">
              {regularTools.length} tool{regularTools.length !== 1 ? 's' : ''}
              {batteries.length > 0 && ` · ${batteries.length} batter${batteries.length !== 1 ? 'ies' : 'y'}`}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setShowBulk(true)}>✨ Add Multiple</button>
          <button className="btn btn-secondary" onClick={() => setShowIntake(true)}>✨ Add One</button>
          <button className="btn btn-secondary" onClick={() => openAdd(true)}>+ Battery</button>
          <button className="btn btn-primary" onClick={() => openAdd(false)}>+ Tool</button>
        </div>
      </div>

      {/* Tools section */}
      {regularTools.length === 0 && batteries.length === 0 ? (
        <EmptyState
          icon="🔧"
          title="No tools tracked yet"
          message="Build a catalog of your tools — drills, saws, levels, and more — so you always know what you have and where it's stored."
          actionLabel="✨ Add Multiple Tools"
          onAction={() => setShowBulk(true)}
        />
      ) : (
        <>
          {regularTools.length > 0 && (
            <>
              {regularTools.length > 0 && (
                <div className="filter-bar">
                  <button className={`filter-btn${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
                    All ({regularTools.length})
                  </button>
                  {TOOL_CATEGORIES.filter((c) => catCounts[c] > 0).map((cat) => (
                    <button key={cat} className={`filter-btn${filter === cat ? ' active' : ''}`} onClick={() => setFilter(cat)}>
                      {CAT_LABEL(cat)} ({catCounts[cat]})
                    </button>
                  ))}
                </div>
              )}
              {displayedTools.length === 0 ? (
                <p className="empty-state">No tools in this category.</p>
              ) : (
                Object.entries(grouped).map(([cat, catTools]) => (
                  <section key={cat} style={{ marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                      {CAT_LABEL(cat)}
                    </h2>
                    <div className="tools-list">
                      {catTools.map((tool) => (
                        <ToolCard
                          key={tool._id}
                          tool={tool}
                          onEdit={() => { setEditTool(tool); setShowForm(true); }}
                          onDelete={() => handleDelete(tool)}
                        />
                      ))}
                    </div>
                  </section>
                ))
              )}
            </>
          )}

          {/* Batteries section */}
          {batteries.length > 0 && (
            <section style={{ marginTop: regularTools.length > 0 ? '2rem' : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: 0 }}>
                  Batteries
                </h2>
                <button className="btn btn-secondary btn-sm" onClick={() => openAdd(true)}>+ Battery</button>
              </div>
              <div className="tools-list">
                {batteries.map((b) => (
                  <BatteryCard
                    key={b._id}
                    tool={b}
                    onEdit={() => { setEditTool(b); setShowForm(true); }}
                    onDelete={() => handleDelete(b)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {showBulk && (
        <ToolBulkIntakeModal
          onClose={() => setShowBulk(false)}
          onAllSaved={(newTools) => {
            newTools.forEach(handleSaved);
            showToast(`${newTools.length} tool${newTools.length !== 1 ? 's' : ''} added`, 'success');
          }}
        />
      )}

      {showIntake && (
        <ToolIntakeModal
          onClose={() => setShowIntake(false)}
          onSaved={(tool) => { handleSaved(tool); showToast('Tool added to inventory', 'success'); }}
        />
      )}

      {showForm && (
        <ToolForm
          existing={editTool}
          onClose={() => { setShowForm(false); setEditTool(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default Tools;
